import { TestIcon } from "@mesketch/svg";
import { Suspense, useCallback, useEffect, useState } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toneOf } from "../components/content.js";
import { CopyableUrl } from "../components/CopyableUrl.js";
import { ErrorBox, NotFound, useNoIndex, useTitle } from "../components/Layout.js";
import { OwnerComposite } from "../components/lazy.js";
import { LoginForm } from "../components/LoginForm.js";
import { headlineOf } from "../components/sketch-parts.js";
import { T, TextsProvider } from "../components/texts.js";
import { PairList } from "../components/Pairs.js";
import { api, type CatalogItem, type OverviewSession, type PairSummary } from "../data/api.js";
import { device, type SessionRecord } from "../data/device.js";
import { refreshIdentity, useIdentity } from "../data/identity.js";
import { progressOf } from "./HomePage.js";

// Moje já `/{jazyk}/me` (REQ-033): nepřihlášeně vysvětlení, e-mail pro odkaz a testy z tohoto
// zařízení; přihlášeně e-mail, odhlášení, společný portrét ze všech testů (CR-021 §6), testy
// identity (pokračovat, otevřít, nový odkaz pro sdílení) a připravená místa pro další části.

/** Nadpis hotového výsledku (jméno typu nebo souhrn) načtený přes identitu. */
function Headline({ resultId }: { resultId: string }) {
  const intl = useIntl();
  const [text, setText] = useState<string | null>(null);
  const [portrait, setPortrait] = useState(false);
  useEffect(() => {
    let live = true;
    api.identityResult(resultId, intl.locale).then(
      (r) => {
        if (!live) return;
        setText(headlineOf(r.payload, r.view, r.texts, r.locale, (id, values) => intl.formatMessage({ id }, values)));
        setPortrait(r.portrait?.status != null);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [resultId, intl]);
  return (
    <>
      {text === null ? null : <p className="me-headline">{text}</p>}
      {/* Hotový test s Portrétem má odkaz na Portrét (REQ-037). */}
      {portrait ? (
        <Link to={`/${intl.locale}/me/results/${resultId}/portrait`} data-testid="me-portrait">
          <FormattedMessage id="me.portrait" />
        </Link>
      ) : null}
    </>
  );
}

function ShareLink({ resultId }: { resultId: string }) {
  const intl = useIntl();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    void api.identityLink(resultId).then((r) => setUrl(`${window.location.origin}/${intl.locale}/r/${r.result_token}`)).catch(() => undefined);
  }, [resultId, intl.locale]);
  return url === null ? null : <CopyableUrl url={url} testId="new-link-url" />;
}

function TestCard({ item, sessions, local }: { item: CatalogItem; sessions: readonly OverviewSession[]; local: readonly SessionRecord[] }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const lang = intl.locale;
  const done = sessions.filter((s) => s.status === "done");
  const open = sessions.find((s) => s.status === "in_progress");
  const [latest, ...older] = done;

  async function resume(s: OverviewSession) {
    const here = local.find((r) => r.id === s.id);
    if (here === undefined) {
      const d = await api.identityDevice(s.id);
      await device.saveSession({ id: d.session_id, token: d.token, slug: d.slug, locale: d.locale, origin: "user", createdAt: new Date().toISOString(), answered: 0, total: 0 });
    }
    void navigate(`/${lang}/s/${s.id}`);
  }

  return (
    <TextsProvider texts={item.texts} locale={lang} preview={false}>
      <section className="card stack" data-testid={`me-test-${item.slug}`}>
        <div className="intro__head">
          <TestIcon tone={toneOf(item.slug)} variant={latest !== undefined ? "color" : "sketch"} progress={open === undefined ? 0 : open.rounds_done / Math.max(1, open.rounds)} size={40} />
          <h2 className="h-small">
            <T k="methodology.name" />
          </h2>
        </div>
        {latest !== undefined && latest.result_id !== null ? (
          <div className="stack" data-testid="me-done" data-result={latest.result_id}>
            <p className="small muted">
              <FormattedMessage id="me.done" values={{ date: <FormattedDate value={latest.finished_at ?? latest.started_at} dateStyle="medium" /> }} />
            </p>
            <Headline resultId={latest.result_id} />
            <Link className="button" to={`/${lang}/me/results/${latest.result_id}`} data-testid="me-open">
              <FormattedMessage id="me.open" />
            </Link>
            <ShareLink resultId={latest.result_id} />
          </div>
        ) : null}
        {open !== undefined ? (
          <div className="stack" data-testid="me-in-progress" data-session={open.id}>
            <p className="small">
              <FormattedMessage id="me.inProgress" values={{ round: Math.min(open.rounds, open.rounds_done + 1), rounds: open.rounds }} />
            </p>
            <button type="button" className="button button--quiet" onClick={() => void resume(open)} data-testid="me-continue">
              <FormattedMessage id="me.continue" />
            </button>
          </div>
        ) : null}
        {latest === undefined && open === undefined ? (
          <p className="small muted">
            <FormattedMessage id="me.notStarted" />{" "}
            {item.available ? (
              <Link to={`/${lang}/test/${item.slug}`}>
                <FormattedMessage id="me.start" />
              </Link>
            ) : null}
          </p>
        ) : null}
        {older.length > 0 ? (
          <details>
            <summary className="small">
              <FormattedMessage id="me.history" />
            </summary>
            <ul className="small">
              {older.map((s) => (
                <li key={s.id}>
                  <FormattedDate value={s.finished_at ?? s.started_at} dateStyle="medium" />{" "}
                  {s.result_id === null ? null : (
                    <Link to={`/${lang}/me/results/${s.result_id}`}>
                      <FormattedMessage id="me.open" />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </section>
    </TextsProvider>
  );
}

/** Páry identity (REQ-033, REQ-034); s vypnutými páry (API 404) se sekce neukáže. */
function Pairs({ pairs, onChange }: { pairs: readonly PairSummary[]; onChange: () => void }) {
  return (
    <section className="card stack" data-testid="me-pairs">
      <h2 className="h-small">
        <FormattedMessage id="me.pairs" />
      </h2>
      {pairs.length === 0 ? (
        <p className="small muted">
          <FormattedMessage id="me.pairs.none" />
        </p>
      ) : (
        <PairList pairs={pairs} owner={null} source="me_page" onChange={onChange} />
      )}
    </section>
  );
}

function Soon({ pairs }: { pairs: boolean }) {
  return (
    <section className="card stack" data-testid="me-soon">
      <h2 className="h-small">
        <FormattedMessage id="me.soon.title" />
      </h2>
      <ul className="stack small">
        {(["portraits", "pairs", "codes", "data"] as const).filter((k) => !(pairs && k === "pairs")).map((k) => (
          <li key={k} data-soon={k}>
            <FormattedMessage id={`me.soon.${k}`} /> · <span className="muted">{<FormattedMessage id="me.soon.note" />}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function MePage() {
  const intl = useIntl();
  const lang = intl.locale;
  const identity = useIdentity();
  const [catalog, setCatalog] = useState<readonly CatalogItem[] | null>(null);
  const [overview, setOverview] = useState<{ email: string; sessions: readonly OverviewSession[] } | null>(null);
  const [local, setLocal] = useState<readonly SessionRecord[]>([]);
  const [pairs, setPairs] = useState<readonly PairSummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [search] = useSearchParams();
  // Návrat po přihlášení jen na cestu aplikace v jazyce stránky (pozvánka do páru, REQ-034).
  const nextParam = search.get("next");
  const next = nextParam !== null && nextParam.startsWith(`/${lang}/`) && !nextParam.startsWith("//") ? nextParam : null;
  useNoIndex();
  useTitle(intl.formatMessage({ id: "me.title" }));

  const load = useCallback(() => {
    setFailed(false);
    void device.sessions().then(setLocal, () => setLocal([]));
    void (async () => {
      try {
        const items = (await api.catalog(lang)).methodologies;
        if (identity.kind !== "in") {
          setCatalog(items);
          return;
        }
        const o = await api.identityOverview();
        // Testy identity mimo katalog (odkazem, unlisted): název z úvodu testu.
        const known = new Set(items.map((i) => i.slug));
        const extra = await Promise.all(
          [...new Set(o.sessions.map((s) => s.slug))]
            .filter((slug) => !known.has(slug))
            .map((slug) =>
              api.intro(slug, lang).then(
                // Test mimo katalog (unlisted) do bloku ikon nepatří (CR-019 §7).
                (intro): CatalogItem => ({ slug, kind: "test", entry: intro.entry, section: "side", available: true, startable: intro.startable, layer: null, rounds: intro.rounds, texts: intro.texts }),
                () => null,
              ),
            ),
        );
        setCatalog([...items, ...extra.filter((e): e is CatalogItem => e !== null)]);
        setOverview(o);
        setPairs(await api.identityPairs().then((r) => r.pairs, () => null));
      } catch {
        setFailed(true);
      }
    })();
  }, [lang, identity.kind]);

  useEffect(() => {
    load();
  }, [load]);

  if (identity.kind === "loading") return null;
  if (identity.kind === "off") return <NotFound />;
  if (failed) return <ErrorBox onRetry={load} />;

  async function signOut(everywhere: boolean) {
    await api.identityLogout(everywhere).catch(() => undefined);
    setOverview(null);
    await refreshIdentity();
  }

  return (
    <div className="stack" data-testid="me">
      <h1>
        <FormattedMessage id="me.title" />
      </h1>
      {identity.kind === "anon" ? (
        <>
          <LoginForm next={next} />
          {catalog !== null && local.length > 0 ? (
            <section className="stack" data-testid="me-device">
              <h2 className="h-small">
                <FormattedMessage id="me.deviceTests" />
              </h2>
              <ul className="stack small">
                {catalog
                  .filter((c) => progressOf(c.slug, local).session !== null)
                  .map((c) => (
                    <li key={c.slug}>
                      <TextsProvider texts={c.texts} locale={lang} preview={false}>
                        <Link to={`/${lang}/test/${c.slug}`}>
                          <T k="methodology.name" />
                        </Link>
                      </TextsProvider>
                    </li>
                  ))}
              </ul>
            </section>
          ) : null}
          {/* Obraz pod přehledem testů, nejvýš 320 px (CR-027 §5). */}
          {local.some((r) => r.origin === "user" && r.done === true) ? (
            <Suspense fallback={null}>
              <OwnerComposite />
            </Suspense>
          ) : null}
        </>
      ) : (
        <>
          <div className="stack">
            <p data-testid="me-email">
              <FormattedMessage id="me.signedAs" values={{ email: identity.email }} />
            </p>
            <div className="me-actions">
              <button type="button" className="link-button" onClick={() => void signOut(false)} data-testid="logout">
                <FormattedMessage id="me.logout" />
              </button>
              <button type="button" className="link-button" onClick={() => void signOut(true)} data-testid="logout-all">
                <FormattedMessage id="me.logoutAll" />
              </button>
            </div>
          </div>
          {catalog !== null && overview !== null ? (
            <>
              <h2>
                <FormattedMessage id="me.tests" />
              </h2>
              {catalog.map((item) => (
                <TestCard key={item.slug} item={item} sessions={overview.sessions.filter((s) => s.slug === item.slug)} local={local} />
              ))}
              {/* Obraz pod přehledem testů, nejvýš 320 px (CR-027 §5). */}
              <Suspense fallback={null}>
                <OwnerComposite />
              </Suspense>
            </>
          ) : null}
          {pairs === null ? null : <Pairs pairs={pairs} onChange={load} />}
          <Soon pairs={pairs !== null} />
        </>
      )}
    </div>
  );
}
